import React, { useEffect, useRef, useState } from 'react';
import GridProduct from "../../products/product/grid-product";
import { getCategoryFromURL, sendGaEventImpressions } from "../../../domain/marketing/gtm-provider";
import { getFromStorage, getRVP, normalizeProduct, saveToStorage } from "../data/data-service";
import { cloneDeep } from "lodash";
import { IResponse } from "../../../interfaces/common-interfaces";
import { initEqualHeight } from '../../list-page/services/list-page-provider';
import { useWindowResizeObserver } from '../../../domain/animations/animations-provider';
import { isMobile } from "../../../domain/common";
import RvpPopup from './rvp-popup';

const GA_EVENTS_TITLE = 'Recently Viewed Products';

const RecentlyViewedProducts: React.FC = () => {
    /**
     * JIRA: https://jira.waves.com:8443/browse/DSWEB-153
     */
    const isAccountPage:boolean = window.location.pathname === '/account/browsing-history';
    const isProductPage:boolean = !!document.querySelector('meta[property="google-ecomm-pagetype"][content="product"]');
    const [products, setProducts] = useState<unknown[]>([]);
    const [areProductExist, setAreProductsExist] = useState(false);
    const top = isAccountPage ? 0 : 5;
    const [isPopupOpened, setIsPopupOpened] = useState(false);
    const [popupType, setPopupType] = useState('');
    const [popupSkuId, setPopupSkuId] = useState(0);
    const [popupProductName, setPopupProductName] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const isMobileScreen = isMobile();

    // handle equal items height
    const containerRef = useRef<HTMLDivElement>(null);
    const [ width ] = useWindowResizeObserver();

    useEffect(() => {
        (async () => {
            if (isAccountPage) {
                // in case of products page - reload products them from database and save to local storage
                let _products:any = [];
                const response : IResponse = await getRVP(top);
                if (response.data.relatedData.length) {
                    for (let i = 0; i < response.data.relatedData.length; i++) {
                        _products.push(normalizeProduct(response.data.relatedData[i]));
                    }
                    sendGAEvents(_products);
                }
                setProducts(_products);
                saveToStorage(null);
                setIsLoaded(true);
                if (_products.length > 0){
                    setAreProductsExist(true);
                }
            } else {
                let _products:any = []; // products
                let ss_products:any = null; // session storage products

                if (!isProductPage && !isAccountPage){
                    ss_products = getFromStorage();
                    if (ss_products && ss_products.length > 0){
                        _products = cloneDeep(ss_products);
                    }
                    sendGAEvents(_products);
                    setProducts(_products);
                    setIsLoaded(true);
                }
                if ((!ss_products || ss_products.length === 0) && !isProductPage && !isAccountPage){
                    const response : IResponse = await getRVP(top);
                    if (response && response.data.relatedData.length > 0) {
                        for (let i = 0; i < response.data.relatedData.length; i++) {
                            _products.push(normalizeProduct(response.data.relatedData[i]));
                        }
                    }
                    saveToStorage(_products);
                    sendGAEvents(_products);
                    setProducts(_products);
                    setIsLoaded(true);

                }
                if (isProductPage){
                    const response : IResponse = await getRVP(top);
                    if (response && response.data.relatedData.length > 0) {
                        for (let i = 0; i < response.data.relatedData.length; i++) {
                            _products.push(normalizeProduct(response.data.relatedData[i]));
                        }
                        saveToStorage(null);
                        sendGAEvents(_products);
                        setProducts(_products);
                        setIsLoaded(true);
                    }
                }
                if (_products.length > 0){
                    setAreProductsExist(true);
                }
            }
        })();
    }, [isAccountPage,isProductPage,top])

    /**
     * send Google Analytics events
     */
    const sendGAEvents = (items: any) => {

        // get first 20 items
        const products = items.slice(0, 20);

        const impressions = products.map((product: any, index: number) => {
            return {
                name: product.documentName
                , id: product.skuNumber
                , price: product.skuPrice
                , category: getCategoryFromURL(product.documentUrlPath)
                , variant: 'NONE'
                , list: GA_EVENTS_TITLE
                , position: index + 1
            };
        });

        sendGaEventImpressions(impressions);
    }

    /**
     * titles and taglines should have equal height in grid view
     */
    useEffect(() => {
        (async () => {
            await initEqualHeight(containerRef.current, width, 'grid', 5, 3, 2);
        })();
    }, [
        width, products,
    ]);

    return (
        <>
            <div
                ref={ containerRef }
                className={`${ isAccountPage ? 'flex md:items-center md:items-start flex-col md:flex-row ' : '' } w-full ${ !isAccountPage && areProductExist ? 'flex mt-32' : ''} ${!isAccountPage && !areProductExist ? ' hidden ' : ''}`}>
                <div className={`${isAccountPage ? 'w-full mx-auto text-left px-5 md:px-0' : 'waves-box-lg mx-auto px-5 md:px-12 text-left'}`}>
                    {
                        !isAccountPage && areProductExist &&
                        <div className="flex waves-h3 py-2 md:py-8">Recently Viewed Products</div>
                    }
                    {
                        isAccountPage && !areProductExist &&
                        <div className="flex justify-start waves-h2">Browsing History</div>
                    }
                    {
                        isAccountPage && areProductExist &&
                        <div className={'flex justify-between px-5 lg:px-4 md:px-4'}>
                            <div className="flex justify-start waves-h2">Browsing History</div>
                            <div className="actions-panel mt-6 flex w-48 flex-col ml-auto">

                                <button
                                    aria-haspopup="dialog"
                                    aria-controls="recently-viewed-products-are-you-sure-popup"
                                    type={"button"}
                                    className={'w-btn'}
                                    onClick={evt => {
                                        evt.preventDefault();
                                        evt.stopPropagation();

                                        setIsPopupOpened(true);
                                        setPopupType('clear-history');
                                        setPopupSkuId(0);
                                        setPopupProductName('');
                                }}>Clear History</button>
                            </div>
                        </div>
                    }

                    {
                        areProductExist && isLoaded &&
                        <div className={`grid gap-4 lg:gap-6 grid-cols-2 md:grid-cols-3 ${!isAccountPage ? 'xl:grid-cols-5' : 'xl:grid-cols-4'} mx-auto ${isMobileScreen ? 'prod-mobile-2col' : ''}`}>
                            {
                                products.map((product:any) => {
                                    return (
                                        <div className={ 'my-4' } key={ product.skuid }>
                                            <GridProduct
                                                listName={ 'Recently Viewed Products' }
                                                classes={ 'h-full' }
                                                isAccountPage={ isAccountPage }
                                                product={ product }
                                                isPriceClockHidden={ true }
                                                isSbsTextHidden={ isAccountPage }>
                                                {
                                                    isAccountPage &&
                                                    <div className="remove-box">
                                                        <button
                                                            aria-haspopup="dialog"
                                                            aria-controls="recently-viewed-products-are-you-sure-popup"
                                                            type={'button'}
                                                            className="w-btn w-auto px-10"
                                                            onClick={evt => {
                                                                evt.preventDefault();
                                                                evt.stopPropagation();
                                                                setPopupProductName(product.documentName);
                                                                setIsPopupOpened(true);
                                                                setPopupType('remove-product');
                                                                setPopupSkuId(product.skuid);
                                                            }}
                                                        >Remove</button>
                                                    </div>
                                                }
                                            </GridProduct>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                    {
                        !isAccountPage && areProductExist &&
                        <div className="mt-6">
                            <a className='no-underline text-gray-700' href="/account/browsing-history">{`View all browsing history >`}</a>
                        </div>
                    }

                    {
                        isPopupOpened &&
                        <RvpPopup
                            popupType={ popupType }
                            setPopupType={ setPopupType }
                            setIsPopupOpened={ setIsPopupOpened }
                            popupSkuId={ popupSkuId }
                            setPopupSkuId={ setPopupSkuId }
                            popupProductName={ popupProductName }
                            setPopupProductName={ setPopupProductName }
                            products={ products }
                            setProducts={ setProducts }
                            setAreProductsExist={ setAreProductsExist }
                        />
                    }
                </div>
            </div>
        </>
    )
}

export default RecentlyViewedProducts;
